// Port of Flutter's globals.dart showMessageDialog(context, title, message):
// a centred, bold title over scrollable body text on the app background colour,
// with a single OK button in the button colour.
//
// Flutter can pop a dialog from anywhere because it has a BuildContext; React
// needs a provider, so mount <MessageDialogProvider> once in the mobile shell
// and call useMessage().show(title, body) from anywhere below it.

import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, useTheme,
} from '@mui/material';

const Ctx = createContext({ show: () => {} });

export function MessageDialogProvider({ children }) {
  const theme = useTheme();
  const c = theme.sli;
  const [msg, setMsg] = useState(null);

  const show = useCallback((title, message) => setMsg({ title, message }), []);
  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog
        open={!!msg}
        onClose={() => setMsg(null)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 280 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: c.text, fontWeight: 700 }}>
          {msg?.title}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <Typography sx={{ color: c.text, fontSize: 16, whiteSpace: 'pre-line' }}>
            {msg?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setMsg(null)}
            sx={{
              bgcolor: c.button,
              color: c.text,
              borderRadius: '10px',
              px: 2.5,
              py: 1.5,
              letterSpacing: '1.2px',
              fontWeight: 600,
              '&:hover': { bgcolor: c.button },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useMessage() {
  return useContext(Ctx);
}
